import { BG } from './constants';

// ── Number formatting ────────────────────────────────────────────────────────
export const fmtNum = (n, d = 2) => {
  if (n == null) return '-';
  return Number(n).toLocaleString('th-TH', {
    minimumFractionDigits: d,
    maximumFractionDigits: d,
  });
};

// ── Input style ──────────────────────────────────────────────────────────────
// disabled=true → ไม่มีกรอบ ดูเหมือนตัวหนังสือธรรมดา ให้ user แยกออกว่าแก้ไม่ได้
// (สีตัวเลขคงเดิมเสมอ ไม่เปลี่ยนเป็นเทา เพื่อให้ยังแยกได้ว่าเป็นรายรับ/รายหักจากสี)
export const mkInput = (color = '#15803d', disabled = false) => ({
  width: '100%',
  textAlign: 'right',
  border: disabled ? '1px solid transparent' : '1px solid #e2e8f0',
  borderRadius: 5,
  padding: '3px 6px',
  fontSize: '0.78rem',
  outline: 'none',
  color,
  background: 'transparent',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s',
  cursor: disabled ? 'default' : 'text',
});

// ── Table cell style helpers ─────────────────────────────────────────────────
export const th0 = (extra = {}) => ({
  fontWeight: 700,
  fontSize: '13px',
  letterSpacing: '0.01em',
  whiteSpace: 'nowrap',
  padding: '12px 8px',
  textAlign: 'center',
  color: BG.headerText,
  borderBottom: `1px solid ${BG.border}`,
  borderRight: `1px solid ${BG.border}`,
  lineHeight: 1.35,
  ...(extra.position === 'sticky' ? { willChange: 'transform' } : {}),
  ...extra,
});

export const td0 = (extra = {}) => ({
  padding: '10px 6px',
  textAlign: 'center',
  fontSize: '13px',
  fontWeight: 400,
  borderRight: `1px solid ${BG.border}`,
  borderBottom: `1px solid ${BG.border}`,
  lineHeight: 1.4,
  color: BG.text,
  verticalAlign: 'middle',
  ...(extra.position === 'sticky' ? { willChange: 'transform' } : {}),
  ...extra,
});

export const dayTh = (dow, isToday, isHoliday) =>
  th0({
    background: isToday
      ? BG.cToday
      : isHoliday
      ? BG.cSaturday
      : dow === 0
      ? BG.cSunday
      : BG.cDay,
    minWidth: 52,
    width: 52,
    padding: '10px 8px',
    fontWeight: 700,
    borderBottom: `2px solid ${BG.border}`,
    borderRight: `1px solid ${BG.border}`,
    position: 'sticky',
    top: 34,
    zIndex: 30,
  });

export const weekendCellBg = (dow, isHoliday) =>
  isHoliday ? BG.saturdayBg : dow === 0 ? BG.sundayBg : null;

export const dayTdBase = (extra = {}) => ({
  padding: '4px 8px',
  textAlign: 'center',
  borderRight: `1px solid ${BG.border}`,
  borderBottom: `1px solid ${BG.border}`,
  verticalAlign: 'middle',
  ...extra,
});

export const dayCellInner = (extra = {}) => ({
  padding: '6px 4px',
  minHeight: 24,
  fontSize: '13px',
  lineHeight: 1.35,
  ...extra,
});

// sticky helpers — disabled (returns {}) เพื่อให้ปิด/เปิดง่าย
export const stickyRight = (_offset) => ({});
export const stickyRightTh = (_offset) => ({});
export const stickyLeft = (_offset) => ({});

// ── Time helpers ─────────────────────────────────────────────────────────────
export const timeToMins = (t) => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + (m || 0);
};

export const minsToTime = (mins) =>
  `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;

// ── Work-hours calc ──────────────────────────────────────────────────────────
// พอร์ตจาก calcEffectiveWorkMinutes (backend/services/timestamp.service.js) — ตัด
// ส่วนหักสายตาม attendanceRules ออก เพราะรายการที่เพิ่มด้วยมือ (is_manual) ไม่หักสายอยู่แล้ว
export const WORK_START_MIN = 8 * 60; // 08:00
export const WORK_END_MIN = 17 * 60; // 17:00
export const NOON_MIN = 12 * 60; // 12:00
export const AFTERNOON_START_MIN = 13 * 60; // 13:00

// เข้าก่อน 08:00 → เริ่มนับ 08:00 / ออกหลัง 17:00 → นับแค่ 17:00
// เข้าช่วง 12:00–12:59 → เริ่มนับ 13:00 เสมอ (ถือว่าผ่านพักเที่ยงไปแล้ว)
// ออกก่อน 13:00 (เข้าก่อนเที่ยง) → cap ที่ 12:00 ไม่หักพักเที่ยง (ยังไม่ทันพัก)
// นอกนั้นหักพักเที่ยง 60 นาที เฉพาะกรณีเข้างานก่อนเที่ยง
export const calcWorkHours = (checkInStr, checkOutStr) => {
  if (!checkInStr || !checkOutStr) return 0;
  const inMins = timeToMins(checkInStr);
  const checkInHour = Math.floor(inMins / 60);

  const isLunchPeriodCheckin = checkInHour === 12;
  const effectiveStart = isLunchPeriodCheckin
    ? AFTERNOON_START_MIN
    : Math.max(inMins, WORK_START_MIN);
  const effectiveEnd = Math.min(timeToMins(checkOutStr), WORK_END_MIN);

  const isAfterLunch = checkInHour >= 12;
  const isEarlyCheckout = !isAfterLunch && effectiveEnd < AFTERNOON_START_MIN;

  let workMins;
  if (isEarlyCheckout) {
    const cappedEnd = effectiveEnd > NOON_MIN ? NOON_MIN : effectiveEnd;
    workMins = Math.max(0, cappedEnd - effectiveStart);
  } else {
    workMins = Math.max(0, effectiveEnd - effectiveStart);
    if (!isAfterLunch) workMins = Math.max(0, workMins - 60);
  }

  return workMins / 60;
};

// แปลงชั่วโมงกลับเป็นเวลาออกงาน (เริ่มนับจาก 08:00) สำหรับ pre-fill dialog
export const hoursToCheckOutTime = (hours) => {
  const h = Math.max(0, Math.min(8, hours || 0));
  const extra = h > 4 ? 1 : 0; // ชดเชยพักเที่ยง 1 ชม. ถ้าทำงานเลยเที่ยง
  return minsToTime(
    Math.min(WORK_END_MIN, WORK_START_MIN + Math.round((h + extra) * 60)),
  );
};

// ── OT calc (single source of truth) ────────────────────────────────────────
// ใช้ร่วมกันโดย: buildOtContent (popup), otTotalMap (column), EmployeeSalaryReport (modal รายงาน)
// - มี manual OT (ot_hours) → ใช้ค่า manual เสมอ (isOverride=true ถ้ามี approved OT ทับอยู่ด้วย)
// - ไม่มี manual แต่มี approved OT (checkin.otRequests) → ใช้ค่า approved
// - ไม่มีทั้งคู่ → ไม่มี OT วันนั้น
// dayHrRate ต้องคำนวณโดยผู้เรียก (getDayHrRate เดิมของแต่ละไฟล์ต่างกันเล็กน้อย ไม่รวมในนี้)
export const calcOtForDay = (checkin, manualEntry, dayHrRate) => {
  const approvedH = checkin?.otRequests?.total_hours || 0;
  const approvedEffH = checkin?.otEffectiveHours || 0;
  const approvedRate =
    approvedH > 0
      ? Math.round((approvedEffH / approvedH) * 100) / 100
      : checkin?.otRequests?.rate || 1.5;
  const manH = manualEntry?.ot_hours || 0;

  if (manH > 0) {
    const manRate = manualEntry.ot_rate || 1.5;
    const manAmt =
      manualEntry.ot_amount != null
        ? manualEntry.ot_amount
        : manH * dayHrRate * manRate;
    return {
      otHours: manH,
      otRate: manRate,
      otAmt: manAmt,
      isManualOt: true,
      isOverride: approvedH > 0,
      approvedHours: approvedH,
      approvedRate,
    };
  }
  if (approvedH > 0) {
    return {
      otHours: approvedH,
      otRate: approvedRate,
      otAmt: approvedEffH * dayHrRate,
      isManualOt: false,
      isOverride: false,
      approvedHours: approvedH,
      approvedRate,
    };
  }
  return {
    otHours: 0,
    otRate: 1.5,
    otAmt: 0,
    isManualOt: false,
    isOverride: false,
    approvedHours: 0,
    approvedRate: 1.5,
  };
};

// ── Salary calc (single source of truth) ────────────────────────────────────
// ใช้ร่วมกันโดย empSummaryMap (คอลัมน์ค่าแรงจริง), buildSalaryContent/buildActualSalaryContent (popup),
// และ buildDailyRowsForReport (modal รายงานค่าแรง) — lv คือ leave record ของวันนั้น (null ถ้าไม่มีลา/ไม่ใช่ FULLTIME)

// "ค่าแรงตามแสดง" (displayAmt) — ใช้ salary_override ถ้ามี (คิดใหม่จาก rate×day_rate ไม่ใช้ amount ที่เซฟไว้ตรงๆ
// เพื่อให้ตรงกับคอลัมน์เสมอแม้ day_rate จะเปลี่ยนทีหลัง), ไม่งั้นคิดจาก 8h (เช้า) / 4h (บ่ายหรือลาครึ่งวัน)
// คืน null ถ้า lv เป็น FULL_DAY (leave ครอบทั้งวัน ไม่นับ checkin วันนั้น)
export const calcCheckinDisplaySalary = (checkin, lv, spd) => {
  if (lv?.period === 'FULL_DAY') return null;
  // ค่าแรงถ้า "ไม่มี" salary_override เลย — คำนวณไว้เสมอ (ไม่ว่าจะ override อยู่จริงหรือไม่)
  // ให้ฝั่งแสดงผล (คอลัมน์ ค่าแรง/วัน ใน modal รายงานค่าแรง) เอาไปเทียบกับค่า override ปัจจุบันได้
  // (ขีดฆ่าค่า default ต่อด้วยค่าที่แก้ใหม่)
  const citHDefault = checkin.checkInTime
    ? parseInt(checkin.checkInTime.split(':')[0], 10)
    : 0;
  const isAfternoonDefault = citHDefault >= 12;
  const defaultHours = lv ? 4 : isAfternoonDefault ? 4 : 8;
  const defaultRate = checkin.effectiveRate || checkin.rate || 1;
  const defaultAmt = (defaultHours / 8) * defaultRate * spd;
  if (checkin.salary_override?.amount != null) {
    const rate = checkin.salary_override.rate ?? checkin.effectiveRate ?? 1;
    const dayRate = checkin.salary_override.day_rate ?? spd;
    const rawAmt = rate * dayRate;
    return {
      amt: lv ? Math.min(rawAmt, spd / 2) : rawAmt,
      hours: checkin.salary_override.hours ?? checkin.totalHour ?? 8,
      rate,
      dayRate,
      isOverride: true,
      defaultAmt,
    };
  }
  return {
    amt: defaultAmt,
    hours: defaultHours,
    rate: defaultRate,
    dayRate: spd,
    isOverride: false,
    isAfternoon: isAfternoonDefault,
    defaultAmt,
  };
};

// "ค่าแรงตามชม.จริง" (actualSalaryAmt) — คิดจากชม.ทำงานจริง (hour_override ถ้ามี ไม่งั้น totalHour)
// cap ที่ 4h เฉพาะกรณีลาครึ่งวัน (ไม่ cap ตามช่วงเข้างานบ่าย เพราะ totalHour จำกัดตัวเองอยู่แล้ว)
// ถ้ามี salary_override (HR แก้ค่าแรง/วันไว้) ให้ใช้ amount/8 เป็นอัตราต่อชม. แทน spd ปกติ —
// ตรงกับ getDayHrRate ที่ OT ใช้อยู่แล้ว กันไม่ให้ OT กับค่าแรงคำนวนจากคนละฐานกัน
// คืน null ถ้า lv เป็น FULL_DAY
export const calcCheckinActualSalary = (checkin, lv, spd) => {
  if (lv?.period === 'FULL_DAY') return null;
  let hours = checkin.hour_override?.hours ?? checkin.totalHour ?? 0;
  if (lv) hours = Math.min(hours, 4);
  // ค่าแรงตามชม.จริงถ้า "ไม่มี" salary_override — คำนวณไว้เสมอด้วยชม.จริงชุดเดียวกัน
  // ให้ popup "สูตรค่าแรง (ชม.จริง)" เอาไปขีดฆ่าเทียบกับค่าที่ override ไว้ได้
  const defaultRate = checkin.effectiveRate || checkin.rate || 1;
  const defaultAmt = (hours / 8) * defaultRate * spd;
  if (checkin.salary_override?.amount != null) {
    const rate =
      checkin.salary_override.rate ?? checkin.effectiveRate ?? checkin.rate ?? 1;
    // day_rate ที่ใช้จริง (ถอยกลับจาก amount/rate) — ให้ label โชว์ตรงกับที่แก้ไว้จริง ไม่ใช่ spd ปกติ
    const dayRate = checkin.salary_override.day_rate ?? checkin.salary_override.amount / rate;
    return {
      amt: (hours / 8) * checkin.salary_override.amount,
      hours,
      rate,
      dayRate,
      isOverride: true,
      defaultAmt,
    };
  }
  return { amt: defaultAmt, hours, rate: defaultRate, dayRate: spd, defaultAmt };
};
